/**
 * Canonical formatting helpers for the VHire admin / ATS UI.
 *
 * Every page MUST use these instead of inline `toLocaleDateString`, ad-hoc
 * CTC tiers, or hand-rolled score-color thresholds. Before this module the
 * same logic was redefined ~6 times with subtle divergence (different CTC
 * tiers, 3- vs 4-band score colors, en-US vs en-IN dates, `-` vs `—` vs ``).
 */

/** Shared empty-value placeholder. */
export const EMPTY = '—';

// ==================== MONEY (INR) ====================

/**
 * Format an INR amount the Indian way: `₹15.0L` / `₹50K` / `₹800`.
 * Non-positive / non-finite / nullish values render the empty placeholder.
 */
export function formatMoney(amount?: number | null): string {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return EMPTY;
  if (amount >= 100_000) return `₹${(amount / 100_000).toFixed(1)}L`;
  if (amount >= 1_000) return `₹${(amount / 1_000).toFixed(0)}K`;
  return `₹${Math.round(amount)}`;
}

/**
 * Format an integer PAISE amount (minor units — how the network/settlement
 * money is stored) the Indian way: `₹15.0L` / `₹50K` / `₹800`.
 *
 * The rest of the money helpers here expect RUPEES, so passing raw paise to
 * formatMoney would over-count by 100×. This converts at the boundary
 * (paise/100) then reuses formatMoney's L/K tiering. Non-positive / non-finite
 * / nullish → the empty placeholder.
 */
export function formatPaise(paise?: number | null): string {
  if (paise == null || !Number.isFinite(paise) || paise <= 0) return EMPTY;
  return formatMoney(paise / 100);
}

/**
 * Exact PAISE → ₹ rendering with Indian digit grouping (`₹1,50,000`) for
 * places that need the precise figure rather than the L/K abbreviation
 * (invoice totals, line items). Non-positive / non-finite / nullish → EMPTY.
 */
export function formatPaiseExact(paise?: number | null): string {
  if (paise == null || !Number.isFinite(paise) || paise <= 0) return EMPTY;
  const rupees = paise / 100;
  const hasFraction = Math.round(rupees) !== rupees;
  return `₹${rupees.toLocaleString('en-IN', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Exact BIGINT-safe PAISE rendering for database decimal-string boundaries.
 * This never passes the value through Number, so pipeline/account aggregates
 * remain precise above Number.MAX_SAFE_INTEGER.
 */
export function formatPaiseDecimal(paise?: string | bigint | null): string {
  if (paise == null || paise === '') return EMPTY;
  let value: bigint;
  try {
    value = typeof paise === 'bigint' ? paise : BigInt(paise);
  } catch {
    return EMPTY;
  }
  if (value <= 0n) return EMPTY;
  const rupees = value / 100n;
  const remainder = value % 100n;
  return `₹${rupees.toLocaleString('en-IN')}${remainder > 0n ? `.${remainder.toString().padStart(2, '0')}` : ''}`;
}

/**
 * Format a CTC value whose canonical unit is LPA (lakhs per annum):
 * `₹12 LPA` / `₹4.5 LPA`.
 *
 * Transition-safe: legacy rows may still hold raw rupees (no plausible LPA
 * exceeds 1000), so rupee-magnitude values are converted on the fly. Use this
 * — not formatMoney — for candidates.current_ctc / expected_ctc.
 */
/**
 * Normalize a mixed-unit CTC column to LPA.
 *
 * `current_ctc` / `expected_ctc` carry BOTH conventions in the same column:
 * some rows are rupees (1500000), some are already LPA (15). Anything at or
 * above 1000 is read as rupees, because nobody earns a four-figure LPA.
 *
 * Exported because display is not the only place that has to agree. Sorting or
 * filtering on the raw number puts a candidate stored as `50` (50 LPA) below
 * one stored as `300000` (3 LPA), and excludes a 15-LPA candidate stored as
 * `1500000` from a "10 to 20 LPA" filter. Display, sort and filter all go
 * through here so they cannot disagree.
 *
 * Returns null for anything unusable, which sorts and filters as "unknown"
 * rather than as zero.
 */
export function ctcToLpa(value?: number | null): number | null {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  return value >= 1000 ? value / 100_000 : value;
}

export function formatCtcLpa(value?: number | null): string {
  const lpa = ctcToLpa(value);
  if (lpa === null) return EMPTY;
  return `₹${lpa % 1 === 0 ? lpa.toFixed(0) : lpa.toFixed(1)} LPA`;
}

// ==================== LABELS ====================

/**
 * Sentence-case a raw enum/status code for display: "pending_review" →
 * "Pending review". The convention for UNKNOWN values (status-config
 * fallbacks, ad-hoc enum rendering) — keeps the raw value recognizable for
 * debugging while never leaking underscores into the UI. Known codes with
 * curated labels (formatSource, formatRole, the status registries) stay
 * title-cased per their own maps; this is only the fallback voice.
 */
export function humanizeLabel(raw: string | null | undefined): string {
  const s = String(raw ?? '').trim().replace(/[\s_-]+/g, ' ');
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Display label for a work-mode code — shared by the applications table and
 *  mobile card so the two surfaces can't drift. */
export function workModeLabel(mode: string | null | undefined): string | null {
  if (!mode) return null;
  if (mode === 'remote') return 'Remote';
  if (mode === 'hybrid') return 'Hybrid';
  if (mode === 'office') return 'Office';
  return mode;
}

// ==================== NAMES ====================

/**
 * Title-case ALL-CAPS person names (NANDAKUMAR RAVIKUMAR → Nandakumar
 * Ravikumar), common in bulk-imported candidate data. Names that already
 * contain a lowercase letter (McKinsey, de Souza) are left untouched so
 * deliberate casing is never mangled; letters after spaces, hyphens and
 * apostrophes are capitalised (D'SOUZA → D'Souza).
 */
export function formatPersonName(name?: string | null): string {
  const trimmed = name?.trim();
  if (!trimmed) return EMPTY;
  if (/[a-z]/.test(trimmed)) return trimmed;
  return trimmed.toLowerCase().replace(/(^|[\s'’-])([a-z])/g, (_m, sep, ch) => sep + ch.toUpperCase());
}

// ==================== DATES ====================

/**
 * Normalize an epoch value (seconds or milliseconds) or an ISO string to
 * epoch milliseconds. Returns null for anything unparseable or non-positive.
 * Handles postgres.js bigints (returned as numeric strings).
 */
export function toEpochMs(ts: number | string | null | undefined): number | null {
  if (ts == null || ts === '' || ts === 0 || ts === '0') return null;
  if (typeof ts === 'string' && /[^0-9.]/.test(ts.trim())) {
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? null : parsed;
  }
  const num = typeof ts === 'string' ? Number(ts) : ts;
  if (!Number.isFinite(num) || num <= 0) return null;
  // Values below 1e12 are treated as seconds; at/above as milliseconds.
  return num < 1e12 ? num * 1000 : num;
}

/**
 * Relative age with sub-day precision for live activity feeds.
 *
 * Buckets: `just now` (< 1 min) / `Xm ago` (< 1 h) / `Xh ago` (< 24 h) /
 * `Xd ago` (< 30 d) / `Xmo ago` (< 12 mo) / `Xy ago`.
 *
 * Invalid input always returns the EMPTY placeholder — never `NaNd ago`,
 * which is the bug this helper exists to prevent. Two ad-hoc copies of this
 * function (`admin/page.tsx` and `candidate-notes-panel.tsx`) were producing
 * "NaNd ago" on the dashboard before being migrated here.
 */
export function formatRelativeDate(ts: number | string | null | undefined): string {
  const ms = toEpochMs(ts);
  if (ms == null) return EMPTY;
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const day = 86_400_000;
  const days = Math.floor(diff / day);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/** Short absolute date in Indian locale: `5 Jan 2026`. */
export function formatShortDate(ts: number | string | null | undefined): string {
  const ms = toEpochMs(ts);
  if (ms == null) return EMPTY;
  return new Date(ms).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** `formatShortDate` on the India calendar, whatever zone the page renders in
 *  (server renders run in UTC). For legal dates: an effective date is stored
 *  as 00:00 IST, which is still the previous day in UTC. */
export function formatShortDateIst(ts: number | string | null | undefined): string {
  const ms = toEpochMs(ts);
  if (ms == null) return EMPTY;
  return new Date(ms).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
}

// ==================== SCORE BANDS ====================

export type ScoreTone = 'strong' | 'good' | 'fair' | 'weak';

/** Four-tier score band, ALIGNED to the match grade thresholds (H1): ≥80 strong (A),
 *  ≥65 good (B), ≥50 fair (C), else weak (D/F). Preserves the coherence invariant
 *  gradeTone(scoreToGrade(s)) === scoreTone(round(s*100)) so a Grade B never renders "fair". */
export function scoreTone(score: number): ScoreTone {
  if (score >= 80) return 'strong';
  if (score >= 65) return 'good';
  if (score >= 50) return 'fair';
  return 'weak';
}

/** Tailwind classes for a score badge/pill, keyed by tone. */
export const SCORE_BADGE_CLASS: Record<ScoreTone, string> = {
  strong: 'bg-green-100 text-green-700',
  good: 'bg-yellow-100 text-yellow-700',
  fair: 'bg-orange-100 text-orange-700',
  weak: 'bg-red-100 text-red-700',
};

/** SVG stroke colors for the score ring, keyed by tone. */
export const SCORE_STROKE: Record<ScoreTone, string> = {
  strong: '#22c55e',
  good: '#eab308',
  fair: '#f97316',
  weak: '#ef4444',
};

/** Human label for a score band. */
export const SCORE_LABEL: Record<ScoreTone, string> = {
  strong: 'Strong Match',
  good: 'Good Match',
  fair: 'Partial Match',
  weak: 'Weak Match',
};

// ==================== QUALITY / LIVENESS / MATCH EXPLAINABILITY (PL2) ====================

/** Match / quality letter grade (A-F). Mirrors the embedding service. */
export type MatchGrade = 'A' | 'B' | 'C' | 'D' | 'F';

/** Recency axis, DISTINCT from quality (good-but-passive talent isn't penalized
 *  on quality). active=green … unknown=muted. */
export type Liveness = 'active' | 'recent' | 'passive' | 'unknown';

/** Per-dimension match breakdown (each 0-1) from the shared scorer. */
export interface MatchBreakdown {
  semantic: number;
  experience: number;
  salary: number;
  location: number;
  availability: number;
  engagement: number;
}

/**
 * PL1/PL2 explainability fields carried per match. Every field optional so rows
 * / cards without the data render unchanged.
 */
export interface MatchExplain {
  grade?: MatchGrade;
  breakdown?: MatchBreakdown;
  /** Confidence brake (PL): when false the candidate's structured profile is too
   *  thin to score — the % / breakdown are a meaningless ~62% and MUST be
   *  suppressed in favor of a "complete profile" treatment (H3). */
  scoreable?: boolean;
  quality?: number;
  qualityGrade?: MatchGrade;
  qualitySignals?: string[];
  qualitySignalsPresent?: number;
  /** Quality computed WITHOUT the assessment signal (renormalized over the
   *  present weight). Grades bias up and silently drop when assessment lands, so
   *  the consumer footnotes it as preliminary (H4). */
  qualityPreliminary?: boolean;
  liveness?: Liveness;
  /** RAW recruiter trust signals behind the quality grade — the un-blended
   *  facts the CandidateSignals chips render. verificationLevel is the
   *  candidate's tier; assessmentScore is 0-100 (null when not assessed). */
  verificationLevel?: 'none' | 'email' | 'phone' | 'kyc' | 'bgv';
  assessmentScore?: number | null;
  matchedSkills?: string[];
  missingSkills?: string[];
  /** Durable candidate-vector readiness, separate from the saved profile. */
  matchReady?: boolean;
  matchReadiness?: 'ready' | 'pending' | 'failed' | 'stale';
  /** Comprehensive scores may use the current saved profile as a live preview. */
  scoreSource?: 'indexed_vector' | 'live_profile_preview';
}

/** Map an A-F grade onto the shared four-tier score tone. */
export function gradeTone(grade: MatchGrade): ScoreTone {
  switch (grade) {
    case 'A': return 'strong';
    case 'B': return 'good';
    case 'C': return 'fair';
    default:  return 'weak'; // D, F
  }
}

/**
 * Quality badge palette — a deliberately DISTINCT violet accent so Quality
 * reads as a second axis next to the green→red Match scale, fading to muted
 * slate at the low end.
 */
export const QUALITY_BADGE_CLASS: Record<ScoreTone, string> = {
  strong: 'bg-violet-200 text-violet-900',
  good: 'bg-violet-100 text-violet-700',
  fair: 'bg-violet-50 text-violet-600',
  weak: 'bg-slate-100 text-slate-500',
};

/** Human label for a liveness bucket. */
export const LIVENESS_LABEL: Record<Liveness, string> = {
  active: 'Active',
  recent: 'Recently active',
  passive: 'Passive',
  unknown: 'Activity unknown',
};

/** Liveness indicator dot color — active=green … unknown=muted. */
export const LIVENESS_DOT_CLASS: Record<Liveness, string> = {
  active: 'bg-green-500',
  recent: 'bg-lime-500',
  passive: 'bg-amber-400',
  unknown: 'bg-muted-foreground/40',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Bucket a `last_active_at` timestamp into a Liveness tier for LivenessDot /
 * CandidateSignals. Mirrors `calcLiveness` in `packages/match-scoring/src/quality.ts`
 * (<=30d active, <=90d recent, >90d passive, null/unparsable unknown) EXACTLY —
 * v4-frontend does not depend on that package (see the H1 scoreToGrade mirror
 * in format.test.ts for the same pattern), so the thresholds are duplicated
 * here rather than cross-imported. Keep the two in lockstep if either changes.
 */
export function calcLiveness(lastActiveAt?: string | null, now: number = Date.now()): Liveness {
  if (!lastActiveAt) return 'unknown';
  const t = new Date(lastActiveAt).getTime();
  if (Number.isNaN(t)) return 'unknown';
  const days = (now - t) / MS_PER_DAY;
  if (days <= 30) return 'active';
  if (days <= 90) return 'recent';
  return 'passive';
}

/** Match-breakdown dimensions that are actually DISPLAYED. Engagement is
 *  intentionally excluded: it's a Quality signal (profile completion + freshness
 *  + verification), weighted 0 in the displayed match score (SEMANTIC_DISPLAY_WEIGHTS),
 *  so rendering it here double-counts Quality and shows an alarming bar with no
 *  effect on the match. The MatchBreakdown type keeps the field — the backend
 *  still returns it; it just isn't a job-FIT dimension we render. */
export type BreakdownDim = Exclude<keyof MatchBreakdown, 'engagement'>;

/** Friendly labels for the match breakdown dimensions. */
export const BREAKDOWN_LABELS: Record<BreakdownDim, string> = {
  semantic: 'Semantic',
  experience: 'Experience',
  salary: 'Salary',
  location: 'Location',
  availability: 'Availability',
};

/** Stable display order for the breakdown bars. */
export const BREAKDOWN_ORDER: ReadonlyArray<BreakdownDim> = [
  'semantic',
  'experience',
  'salary',
  'location',
  'availability',
];

/**
 * One-line plain-English verdict for the "Why this match" card. Leads with the
 * grade, then the must-have skill coverage — deliberately surfacing the common
 * tension where a high grade (saturated semantic/experience/location) hides a
 * real hard-skill gap. Returns null when there's nothing honest to say (an
 * unscoreable profile, or neither a grade nor any skill data).
 */
export function matchVerdict(m: MatchExplain | null | undefined): string | null {
  if (!m || m.scoreable === false) return null;
  const matched = m.matchedSkills?.length ?? 0;
  const missing = m.missingSkills?.length ?? 0;
  const total = matched + missing;

  const leadByGrade: Record<MatchGrade, string> = {
    A: 'Strong overall match',
    B: 'Solid match',
    C: 'Partial match',
    D: 'Weak match',
    F: 'Weak match',
  };

  const parts: string[] = [];
  if (m.grade) parts.push(leadByGrade[m.grade]);

  if (total > 0) {
    const cov = `${matched} of ${total} must-have skill${total === 1 ? '' : 's'}`;
    if (matched < total) {
      parts.push(parts.length ? `but covers only ${cov}` : `Covers only ${cov}`);
    } else {
      parts.push(parts.length ? `covering all ${cov}` : `Covers all ${cov}`);
    }
  }

  if (parts.length === 0) return null;
  return parts.join(', ') + (total > 0 && missing > 0 ? ' — review the gaps below.' : '.');
}

// ==================== NAMES / AVATARS ====================

/** Two-letter uppercase initials, resilient to extra whitespace. */
export function getInitials(name?: string | null): string {
  if (!name) return 'U';
  const letters = name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('');
  return letters.slice(0, 2).toUpperCase() || 'U';
}

const AVATAR_GRADIENTS = [
  'from-blue-500 to-purple-600',
  'from-green-500 to-teal-600',
  'from-orange-500 to-red-600',
  'from-pink-500 to-rose-600',
  'from-indigo-500 to-blue-600',
  'from-cyan-500 to-blue-600',
];

/** Deterministic avatar gradient derived from a name. */
export function avatarGradient(name?: string | null): string {
  const idx = (name?.charCodeAt(0) || 0) % AVATAR_GRADIENTS.length;
  return AVATAR_GRADIENTS[idx];
}

// ==================== LOCATION ====================

/** Parse a location that may be a JSON blob (`{"city":...}`) or a plain string. */
export function parseLocation(loc?: string | null): string {
  if (!loc) return '';
  if (typeof loc !== 'string') return String(loc);
  if (loc.trimStart().startsWith('{')) {
    try {
      const p = JSON.parse(loc);
      return p.address || p.city || p.state || '';
    } catch {
      return loc;
    }
  }
  return loc;
}

// ==================== SKILLS ====================

function skillName(s: unknown): string {
  if (typeof s === 'string') return s.trim();
  if (s && typeof s === 'object') {
    return String((s as { name?: unknown }).name ?? '').trim();
  }
  return String(s ?? '').trim();
}

/** Parse skills from a JSON-string array, an actual array, or a comma list. */
export function parseSkills(raw?: string | string[] | null): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(skillName).filter(Boolean);
  const str = String(raw);
  if (str.trimStart().startsWith('[')) {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) return parsed.map(skillName).filter(Boolean);
    } catch {
      /* fall through to comma split */
    }
  }
  return str
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Parse the candidate `languages` field into a flat string[]. Tolerates every
 * shape the API has been observed to ship: a real string[], an array of
 * `{name|language}` objects, a JSON-string of either, or a comma-separated
 * string. Returns [] for null/undefined/anything unparseable so callers can
 * always do `.map` without a guard. Add new shapes here, not in pages.
 */
export function parseLanguages(
  raw?: string | string[] | Array<{ name?: string; language?: string }> | null,
): string[] {
  if (!raw) return [];
  const toLabel = (l: unknown): string => {
    if (typeof l === 'string') return l;
    if (l && typeof l === 'object') {
      const obj = l as { name?: string; language?: string };
      return obj.name || obj.language || '';
    }
    return '';
  };
  if (Array.isArray(raw)) {
    return raw.map(toLabel).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const str = raw.trim();
    if (str.startsWith('[')) {
      try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) return parsed.map(toLabel).filter(Boolean);
      } catch {
        /* fall through to comma split */
      }
    }
    return str.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

const skillKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
const capFirst = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

const SKILL_CANONICAL: Record<string, string> = {
  aws: 'AWS',
  gcp: 'GCP',
  azure: 'Azure',
  sql: 'SQL',
  nosql: 'NoSQL',
  api: 'API',
  rest: 'REST',
  restapi: 'REST API',
  graphql: 'GraphQL',
  html: 'HTML',
  css: 'CSS',
  scss: 'SCSS',
  sass: 'Sass',
  json: 'JSON',
  xml: 'XML',
  yaml: 'YAML',
  sdk: 'SDK',
  cli: 'CLI',
  ui: 'UI',
  ux: 'UX',
  cicd: 'CI/CD',
  devops: 'DevOps',
  ios: 'iOS',
  macos: 'macOS',
  android: 'Android',
  net: '.NET',
  dotnet: '.NET',
  aspnet: 'ASP.NET',
  nodejs: 'Node.js',
  node: 'Node.js',
  reactjs: 'React',
  react: 'React',
  reactnative: 'React Native',
  nextjs: 'Next.js',
  vuejs: 'Vue.js',
  vue: 'Vue.js',
  angularjs: 'Angular',
  angular: 'Angular',
  typescript: 'TypeScript',
  javascript: 'JavaScript',
  js: 'JavaScript',
  ts: 'TypeScript',
  php: 'PHP',
  golang: 'Go',
  go: 'Go',
  cpp: 'C++',
  csharp: 'C#',
  c: 'C',
  ruby: 'Ruby',
  rails: 'Rails',
  ror: 'Ruby on Rails',
  postgresql: 'PostgreSQL',
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  mongodb: 'MongoDB',
  mongo: 'MongoDB',
  redis: 'Redis',
  kubernetes: 'Kubernetes',
  k8s: 'Kubernetes',
  docker: 'Docker',
  terraform: 'Terraform',
  jwt: 'JWT',
  oauth: 'OAuth',
  saas: 'SaaS',
  etl: 'ETL',
  llm: 'LLM',
  ai: 'AI',
  ml: 'ML',
  nlp: 'NLP',
  tcp: 'TCP',
  http: 'HTTP',
  https: 'HTTPS',
  grpc: 'gRPC',
  seo: 'SEO',
  crm: 'CRM',
  erp: 'ERP',
  qa: 'QA',
};

/**
 * Canonical-map-aware title-case: preserves known acronyms/tech tokens
 * (AWS, .NET, iOS, CI/CD, Node.js) and title-cases the rest
 * (react_native → 'React Native'). Unknown tokens are capitalized, never
 * worse than a naive title-case.
 */
export function normalizeSkill(skill: string): string {
  if (!skill) return '';
  const raw = skill.trim();
  if (!raw) return '';
  const whole = SKILL_CANONICAL[skillKey(raw)];
  if (whole) return whole;
  return raw
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((seg) => SKILL_CANONICAL[skillKey(seg)] ?? capFirst(seg.toLowerCase()))
    .join(' ');
}

// ==================== SOURCE ====================

/** Human label for a candidate/application source code. */
export function formatSource(raw?: string | null): string {
  if (!raw) return EMPTY;
  const k = String(raw).trim().toLowerCase();
  const MAP: Record<string, string> = {
    ats_import: 'ATS Import',
    mysql_import: 'Legacy Import',
    direct_login: 'Direct Signup',
    oauth_login: 'OAuth Login',
    career_site: 'Career Site',
    naukri: 'Naukri',
    linkedin: 'LinkedIn',
    referral: 'Referral',
    marketplace: 'Marketplace',
  };
  if (MAP[k]) return MAP[k];
  if (k.startsWith('etl')) return 'ETL Import';
  // Fallback: humanize an unknown code ("some_source" → "Some Source").
  return k
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ==================== ROLE ====================

/**
 * Human label for a tenant role slug. Prefers a provided catalog label
 * (custom per-tenant roles), then maps known system roles, then title-cases
 * the slug. Prevents raw enum values like "tenant_owner" (which CSS
 * `capitalize` renders as "Tenant_owner") leaking into the UI.
 */
export function formatRole(
  raw?: string | null,
  roles?: { value: string; label: string }[],
): string {
  if (!raw) return EMPTY;
  const known = roles?.find((r) => r.value === raw);
  if (known?.label) return known.label;
  const k = String(raw).trim().toLowerCase();
  const MAP: Record<string, string> = {
    tenant_owner: 'Owner',
    owner: 'Owner',
    tenant_admin: 'Admin',
    admin: 'Admin',
    recruiter: 'Recruiter',
    hiring_manager: 'Hiring Manager',
    team_manager: 'Team Manager',
    viewer: 'Viewer',
  };
  if (MAP[k]) return MAP[k];
  return k
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
